'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, CheckCircle } from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographyP, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';
import type { ProjectNeighborhood } from '../../data/project-data';

const SECTION_ID = 'project-neighborhood';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const itemMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

interface ProjectNeighborhoodSectionProperties {
  neighborhood: ProjectNeighborhood;
  projectName: string;
}

function ProjectNeighborhoodSection({
  neighborhood,
  projectName,
}: ProjectNeighborhoodSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="LOCATION & NEIGHBORHOOD"
          title={`${neighborhood.areaName} Living`}
          description={`Explore the community and surroundings near the ${projectName} project.`}
        />

        <div className={cls`grid grid-cols-1 md:grid-cols-2 gap-8 w-full`}>
          {/* Text content */}
          <motion.div
            className={cls`flex flex-col gap-6`}
            {...itemMotionProps}
          >
            <TypographyP className="text-foreground leading-relaxed">
              {neighborhood.description}
            </TypographyP>

            {/* Highlights */}
            <div className="flex flex-col gap-2">
              <TypographyH4 className="text-foreground">
                Area Highlights
              </TypographyH4>
              <ul className="flex flex-col gap-2">
                {neighborhood.highlights.map((highlight) => (
                  <li key={highlight} className="flex items-start gap-2">
                    <CheckCircle className="text-primary size-5 mt-0.5 shrink-0" />
                    <TypographySmall className="text-muted-foreground">
                      {highlight}
                    </TypographySmall>
                  </li>
                ))}
              </ul>
            </div>

            {/* Nearby landmarks */}
            <div className="flex flex-col gap-2">
              <TypographyH4 className="text-foreground">
                Nearby Landmarks
              </TypographyH4>
              <ul className="flex flex-col gap-2">
                {neighborhood.nearbyLandmarks.map((landmark) => (
                  <li key={landmark} className="flex items-start gap-2">
                    <MapPin className="text-primary size-5 mt-0.5 shrink-0" />
                    <TypographySmall className="text-muted-foreground">
                      {landmark}
                    </TypographySmall>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Map embed */}
          <motion.div
            className={cls`w-full min-h-[300px] rounded-lg overflow-hidden`}
            {...itemMotionProps}
          >
            <iframe
              title={`Map of ${neighborhood.areaName}`}
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d51444.95865498607!2d-95.64!3d36.31!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87b6ed364f1e2d03%3A0x4e1e7b2d58558bce!2sClaremore%2C%20OK!5e0!3m2!1sen!2sus!4v1700000000000"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '300px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default ProjectNeighborhoodSection;
